//
// README - Beta Component
//
// This component is marked "beta" and will go through some iterations
// before landing at a final solution. Please reach out to the Cadence team
// if you intend to use this in the meantime and we'll help you out!
//

import React from 'react';
import classNames from 'classnames';
import { RCStarRating } from '../';
import ExpeditedShippingTruckIcon from '../../images/nudge/fast-truck-tiny.svg?component';

export type RCSignalType = {
  name: string;
  title: string;
  icon: string;
};

interface LinkElementProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  className?: string;
  children?: React.ReactNode;
}

interface ListingProps {
  /** Url for the listing image. Should be at least 300px wide. */
  thumbnailURL: string;

  /** Alt text of image (if available) for a11y purposes */
  thumbnailAlt: string;

  /** Main title for the listing */
  title: string;

  /** The condition of a listing, written in plain text */
  condition: string;

  /** Original price will have strike-through styles applied when present alongside `price` */
  originalPrice?: string;

  /** Text that appears in red adjacent to the original price */
  discountText?: string;

  /** The current sale price of the listing */
  price: string;

  /** Renders the "truck" icon next to the shipping price */
  expeditedShipping?: boolean;

  /** The current shipping price of the listing. Consumer must format properly for paid and free shipping. */
  shippingPrice?: string;

  /** Name of the shop */
  shopName: string;

  /** Renders the rating stars. Must be a number 0 to 1 */
  shopRating: number;

  /** Location of the shop */
  shopLocation: string;

  /** Renders a formatted list of signals. Consumer is responsible for rendering the correct number of signals on a given card */
  signals?: RCSignalType[];
}

interface CardProps {
  /** To help assistive technologies make sense of the pricing block.
   * This should be human-readable and contain details about price, original price, and discounts.
   * Example: `Originally $10, now $7 ($3 price drop)`
  */
  a11yPriceDescription: string;

  /** The link element (e.g. CoreLink)
   * Example: `linkElement={(children) => (<CoreLink to="/whatever/url/">{children}</CoreLink>)}`
  */
  linkElement?: (children: React.ReactNode) => React.ReactElement<LinkElementProps>;

  /** The main action on the card, likely a primary "Add to Cart" RCButton */
  primaryAction?: React.ReactElement;

  /** The secondary action, likely a secondary "Make an Offer" RCButton */
  secondaryAction?: React.ReactElement;

  /** Pivot the element that the title renders inside of for semantic, a11y and SEO purposes. If in doubt, leave as default. */
  TitleElement?: 'h3' | 'h4' | 'div';

  /** When true, cards will all be the same height when inside a flexbox-enabled grid */
  verticalStretch?: boolean;

  /** The watch element that gets rendered on top of the thumbnail in the upper-right corner of the card */
  watchButtonElement?: React.ReactElement;

  /** Renders a wire-framed variant of the card when data isn't available yet */
  loading?: boolean;
}

export type RCComparisonCardProps = ListingProps & CardProps;

export function RCComparisonCard({
  a11yPriceDescription,
  condition,
  discountText = undefined,
  expeditedShipping = false,
  linkElement = undefined,
  signals = undefined,
  originalPrice = undefined,
  price,
  primaryAction = undefined,
  secondaryAction = undefined,
  shippingPrice = undefined,
  shopLocation,
  shopName,
  shopRating,
  thumbnailAlt,
  thumbnailURL,
  title,
  TitleElement = 'h3',
  verticalStretch = true,
  watchButtonElement = undefined,
  loading = false,
}: RCComparisonCardProps) {
  function PassedLink({ children, className, ...passthroughProps }: LinkElementProps) {
    if (!linkElement) {
      return <>{children}</>;
    }

    return React.cloneElement(
      linkElement(children),
      {
        className: className,
        ...passthroughProps,
      },
    );
  }

  function LoadingStateUI() {
    return (
      <div className="rc-comparison-card rc-comparison-card--loading">
        <div className="rc-comparison-card__thumbnail" />
        <div className="rc-comparison-card__body">
          <div className="rc-comparison-card__titleblock">
            <div className="rc-comparison-card__title" />
            <div className="rc-comparison-card__condition" />
          </div>
          <div className="rc-comparison-card__price-block">
            <div className="rc-comparison-card__price" />
            <div className="rc-comparison-card__shipping-price" />
          </div>
          <div className="rc-comparison-card__shop">
            <div className="rc-comparison-card__shop-name" />
            <div className="rc-comparison-card__shop-location" />
          </div>
          <div className="rc-comparison-card__actions">
            <div className="rc-comparison-card__action" />
            <div className="rc-comparison-card__action" />
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return <LoadingStateUI />;
  }

  return (
    <div
      className={classNames(
        'rc-comparison-card',
        {
          'rc-comparison-card--reduced-price': originalPrice,
          'rc-comparison-card--stretch': verticalStretch,
          'rc-comparison-card--clickable': linkElement,
          'rc-comparison-card--expedited': expeditedShipping,
        },
      )}
    >
      <PassedLink className="rc-comparison-card__link">
        <img
          className="rc-comparison-card__thumbnail"
          src={thumbnailURL}
          alt={thumbnailAlt}
        />
      </PassedLink>
      <div className="rc-comparison-card__body">

        <div className="rc-comparison-card__titleblock">
          <PassedLink
            className="rc-comparison-card__link rc-comparison-card__link--title"
            title={title}
          >
            <TitleElement className="rc-comparison-card__title">
              {title}
            </TitleElement>
          </PassedLink>
          <span className="rc-comparison-card__condition">
            {condition}
          </span>
        </div>

        <div className="rc-comparison-card__price-block">
          <span className="visually-hidden">
            {a11yPriceDescription}
          </span>
          {originalPrice &&
            <div className="rc-comparison-card__discount-block" aria-hidden>
              <span className="rc-comparison-card__original-price">
                {originalPrice}
              </span>
              {discountText &&
                <span className="rc-comparison-card__discount-text">
                  {discountText}
                </span>
              }
            </div>
          }
          <span
            className="rc-comparison-card__price"
            aria-hidden
          >
            {price}
          </span>
          <span className="rc-comparison-card__shipping-price">
            {expeditedShipping &&
              <ExpeditedShippingTruckIcon />
            }
            {shippingPrice}
          </span>
        </div>

        <div className="rc-comparison-card__shop">
          <div className="rc-comparison-card__shop-titleblock">
            <span className="rc-comparison-card__shop-name">
              {shopName}
            </span>
            {(shopRating > 0) &&
              <div className="rc-comparison-card__shop-rating">
                <RCStarRating
                  rating={shopRating}
                  hideTooltip
                />
              </div>
            }
          </div>
          <span className="rc-comparison-card__shop-location">
            {shopLocation}
          </span>
        </div>

        {(signals?.length > 0) &&
          <div className="rc-comparison-card__signals">
            {signals.map(signal => (
              <div
                key={signal.title}
                className="rc-comparison-card__signal"
              >
                <img
                  className="rc-comparison-card__signal__icon"
                  src={signal.icon}
                  alt=""
                />
                <span className="rc-comparison-card__signal__title">
                  {signal.title}
                </span>
              </div>
            ))}
          </div>
        }

        {(primaryAction || secondaryAction) &&
          <div className="rc-comparison-card__actions">
            {secondaryAction &&
              <div className="rc-comparison-card__action rc-comparison-card__action--secondary">
                {secondaryAction}
              </div>
            }
            {primaryAction &&
              <div className="rc-comparison-card__action rc-comparison-card__action--primary">
                {primaryAction}
              </div>
            }
          </div>
        }

        {watchButtonElement &&
          <div className="rc-comparison-card__watch-container">
            {watchButtonElement}
          </div>
        }
      </div>
    </div>
  );
}
